import { useEffect, useState } from "react";
import { Container, Form, OverlayTrigger, Tooltip } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import Edit from "../../../../components/DstIcons/Edit";
import Search from "../../../../components/DstIcons/Search";
import Lister from "../../../../components/Lister/Lister";
import Popup from "../../../../components/Popup/Popup";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { DiplomaRegenerateForm, UserDocumentsProps } from "./UserDocuments.d";
import { fetchUserDocuments, getDiplomaFormValues, regenerateDiploma } from "./UserDocuments.function";

const _documentTypeLabel = (language: string, type: string) =>
  type === "All"
    ? translate(language, "PAGES.USER.DOCUMENT_TABLE.TYPES.ALL")
    : translate(language, `PAGES.USER.DOCUMENT_TABLE.TYPES.${type.toUpperCase()}`) || type;

const UserDocuments = ({ userId, language, refreshUserDocuments }: UserDocumentsProps): JSX.Element => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDiploma, setSelectedDiploma] = useState<any>(null);
  const [diplomaForm, setDiplomaForm] = useState<DiplomaRegenerateForm | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState("");
  const documentTypes = ["All"].concat(Array.from(new Set(documents.map((document) => document.type).filter(Boolean))));

  useEffect(() => {
    fetchUserDocuments(userId, setDocuments, setLoading);
  }, [userId]);

  const _closeEditModal = () => {
    if (submitting) return;
    setShowEditModal(false);
    setSelectedDiploma(null);
    setDiplomaForm(null);
  };

  const _openDiplomaEditModal = (document: any) => {
    setSelectedDiploma(document);
    setDiplomaForm(getDiplomaFormValues(document));
    setShowEditModal(true);
  };

  return (
    <Container fluid>
      <div className="mb-3">
        {!loading ? (
          documentTypes.map((type: string) => (
            <DstButton
              key={type}
              variant="info"
              clickFunction={() => setFilterType(type === "All" ? "" : type)}
              btnClass={`me-2 ${
                filterType === type || (filterType === "" && type === "All") ? "active-filter-btn" : ""
              }`}
              value={_documentTypeLabel(language, type)}
            />
          ))
        ) : (
          <div className="d-flex">
            <DstButton mock btnWidth="41px" btnClass="me-2" />
            <DstButton mock btnWidth="90px" />
          </div>
        )}
      </div>
      {!loading ? (
        <Lister
          items={documents.filter((user_document: any) => ["", user_document.type].includes(filterType))}
          columns={[
            {
              header: translate(language, "PAGES.USER.DOCUMENT_TABLE.NAME"),
              key: "name",
              sortable: true,
            },
            {
              header: translate(language, "PAGES.USER.DOCUMENT_TABLE.DATE"),
              key: "dt_created",
              render: (item: any) => (
                <div>
                  <OverlayTrigger overlay={<Tooltip>{formatDateHumanly(language, item["dt_created"], true)}</Tooltip>}>
                    <span>{formatDateHumanly(language, item["dt_created"])}</span>
                  </OverlayTrigger>
                </div>
              ),
              sortable: true,
              sortType: "date",
            },
            {
              header: translate(language, "PAGES.USER.DOCUMENT_TABLE.UPDATED_DATE"),
              key: "dt_updated",
              render: (item: any) => (
                <div>
                  {item.dt_updated ? (
                    <OverlayTrigger overlay={<Tooltip>{formatDateHumanly(language, item.dt_updated, true)}</Tooltip>}>
                      <span>{formatDateHumanly(language, item.dt_updated)}</span>
                    </OverlayTrigger>
                  ) : (
                    <span className="text-muted">N/A</span>
                  )}
                </div>
              ),
              sortable: true,
              sortType: "date",
            },
          ]}
          additionalButton={(item: any) => (
            <span className="d-inline-flex align-items-center">
              <span
                className="clickable"
                onClick={() => window.open(`${process.env.REACT_APP_FILE_URL}/${item.type}/${item.filename}`, "_blank")}
              >
                <Search classes="search-icon" />
              </span>
              {item.type === "diploma" && (
                <PermissionComponent
                  neededPermission={[{ methods: ["POST"], route: "/documents/diplomas/*/regenerate" }]}
                >
                  <span className="clickable ms-2" onClick={() => _openDiplomaEditModal(item)}>
                    <Edit classes="search-icon" color="var(--color-light-silver)" />
                  </span>
                </PermissionComponent>
              )}
            </span>
          )}
        />
      ) : (
        <Lister
          columns={[
            { header: translate(language, "PAGES.USER.DOCUMENT_TABLE.NAME"), key: "name" },
            { header: translate(language, "PAGES.USER.DOCUMENT_TABLE.DATE"), key: "dt_created" },
            { header: translate(language, "PAGES.USER.DOCUMENT_TABLE.UPDATED_DATE"), key: "dt_updated" },
          ]}
          mock
        />
      )}
      <Popup
        show={showEditModal}
        onClose={_closeEditModal}
        modalTitle={translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.TITLE")}
        scrollable
      >
        <div className="m-regular">
          {diplomaForm && (
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                if (!selectedDiploma?.id || !diplomaForm.birthdate) return;
                regenerateDiploma(language, selectedDiploma.id, diplomaForm, setSubmitting, () => {
                  setShowEditModal(false);
                  setSelectedDiploma(null);
                  setDiplomaForm(null);
                  fetchUserDocuments(userId, setDocuments, setLoading);
                  refreshUserDocuments?.();
                });
              }}
            >
              <Form.Group className="mb-3">
                <Form.Label>{translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.NAME")}</Form.Label>
                <Form.Control
                  type="text"
                  value={diplomaForm.name}
                  onChange={(event) => setDiplomaForm({ ...diplomaForm, name: event.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.BIRTHDATE")}</Form.Label>
                <Form.Control
                  type="date"
                  value={diplomaForm.birthdate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(event) => setDiplomaForm({ ...diplomaForm, birthdate: event.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.DIPLOMA_DATE")}</Form.Label>
                <Form.Control
                  type="date"
                  value={diplomaForm.date}
                  onChange={(event) => setDiplomaForm({ ...diplomaForm, date: event.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.LANGUAGE")}</Form.Label>
                <Form.Select
                  value={diplomaForm.language}
                  onChange={(event) => setDiplomaForm({ ...diplomaForm, language: event.target.value })}
                >
                  <option value="FR">
                    {translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.LANGUAGE_FR")}
                  </option>
                  <option value="EN">
                    {translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.LANGUAGE_EN")}
                  </option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.TYPE")}</Form.Label>
                <Form.Check
                  type="radio"
                  id="diploma-type-standard"
                  name="diploma-type"
                  className="mb-1"
                  label={translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.TYPE_LIORA")}
                  checked={!diplomaForm.is_mines && !diplomaForm.is_ece}
                  onChange={() => setDiplomaForm({ ...diplomaForm, is_mines: false, is_ece: false })}
                />
                <Form.Check
                  type="radio"
                  id="diploma-type-mines"
                  name="diploma-type"
                  className="mb-1"
                  label={translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.IS_MINES")}
                  checked={diplomaForm.is_mines}
                  onChange={() => setDiplomaForm({ ...diplomaForm, is_mines: true, is_ece: false })}
                />
                <Form.Check
                  type="radio"
                  id="diploma-type-ece"
                  name="diploma-type"
                  className="mb-1"
                  label={translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.IS_ECE")}
                  checked={diplomaForm.is_ece}
                  onChange={() => setDiplomaForm({ ...diplomaForm, is_mines: false, is_ece: true })}
                />
              </Form.Group>
              <Form.Check
                className="mb-3"
                type="checkbox"
                id="diploma-send-email"
                label={translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.SEND_EMAIL")}
                checked={diplomaForm.send_email}
                onChange={(event) => setDiplomaForm({ ...diplomaForm, send_email: event.target.checked })}
              />
              <DstButton
                type="submit"
                value={translate(language, "PAGES.USER.DOCUMENT_TABLE.EDIT_DIPLOMA.SUBMIT")}
                btnClass="mt-3 mt-md-5 mx-auto d-block"
                loading={submitting}
                disabled={!diplomaForm.birthdate || submitting}
              />
            </Form>
          )}
        </div>
      </Popup>
    </Container>
  );
};

export default UserDocuments;
